import React, { forwardRef, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AnimatePresence, m } from 'framer-motion';
import { X, AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';

export const cx = (...c) => c.filter(Boolean).join(' ');

/* ── Button ─────────────────────────────────────────────────────────────── */
const VARIANTS = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover shadow-sm',
  secondary: 'bg-surface text-ink ring-1 ring-inset ring-line-strong hover:bg-paper-sunk',
  ghost: 'bg-transparent text-ink hover:bg-ink/5',
  inverse: 'bg-white text-night hover:bg-white/90',
  outlineInverse: 'bg-transparent text-white ring-1 ring-inset ring-white/40 hover:bg-white/10',
  danger: 'bg-signal-bad text-white hover:bg-[#a62d23]',
  brand: 'bg-[#2B528F] text-white hover:bg-[#1E3A6B]',
};
const SIZES = {
  sm: 'h-9 px-4 text-sm gap-1.5',
  md: 'h-11 px-5 text-[0.95rem] gap-2',
  lg: 'h-14 px-7 text-base gap-2',
};

export const Spinner = ({ className = 'h-4 w-4' }) => (
  <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const Button = forwardRef(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, to, href, type = 'button', full, ...rest },
  ref,
) {
  const cls = cx(
    'relative inline-flex select-none items-center justify-center rounded-pill font-medium transition',
    'duration-150 ease-out active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
    VARIANTS[variant], SIZES[size], full && 'w-full', className,
  );
  const content = (
    <>
      {loading && <span className="absolute inset-0 flex items-center justify-center"><Spinner /></span>}
      <span className={cx('inline-flex items-center gap-[inherit]', loading && 'invisible')}>{children}</span>
    </>
  );
  if (to) return <Link ref={ref} to={to} className={cls} {...rest}>{content}</Link>;
  if (href) return <a ref={ref} href={href} className={cls} {...rest}>{content}</a>;
  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {content}
    </button>
  );
});

/* ── Layout primitives ──────────────────────────────────────────────────── */
export const Container = ({ className, children, ...rest }) => (
  <div className={cx('container-page', className)} {...rest}>{children}</div>
);

export const Section = ({ tone = 'light', className, children, id }) => (
  <section id={id} className={cx(
    'py-16 sm:py-24',
    tone === 'dark' && 'bg-night text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white',
    tone === 'sunk' && 'bg-paper-sunk',
    className,
  )}>{children}</section>
);

export const Eyebrow = ({ children, className, dark }) => (
  <p className={cx('eyebrow', dark && 'text-white/60', className)}>{children}</p>
);

export const PageHeader = ({ eyebrow, title, intro, children, align = 'left' }) => (
  <header className={cx('pt-32 pb-10 sm:pt-40 sm:pb-14', align === 'center' && 'text-center')}>
    <Container>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className={cx('mt-3 text-title', align === 'center' && 'mx-auto max-w-4xl')}>{title}</h1>
      {intro && <p className={cx('mt-5 max-w-2xl text-lg text-ink-muted', align === 'center' && 'mx-auto')}>{intro}</p>}
      {children}
    </Container>
  </header>
);

export const Card = ({ className, children, as: Tag = 'div', ...rest }) => (
  <Tag className={cx('rounded-3xl bg-surface p-6 shadow-card ring-1 ring-line sm:p-8', className)} {...rest}>{children}</Tag>
);

export const Badge = ({ tone = 'neutral', children, className }) => {
  const tones = {
    neutral: 'bg-paper-sunk text-ink-soft ring-line',
    ok: 'bg-signal-ok-soft text-signal-on-ok ring-signal-ok/25',
    warn: 'bg-signal-warn-soft text-signal-on-warn ring-signal-warn/25',
    bad: 'bg-signal-bad-soft text-signal-on-bad ring-signal-bad/25',
    brand: 'bg-accent-soft text-accent-on ring-accent/25',
    dark: 'bg-white/10 text-white ring-white/20',
  };
  return <span className={cx('inline-flex items-center gap-1 rounded-pill px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset', tones[tone], className)}>{children}</span>;
};

/* ── Alerts ─────────────────────────────────────────────────────────────── */
export const Alert = ({ tone = 'info', title, children, className }) => {
  const map = {
    info: ['bg-accent-soft text-accent-on ring-accent/20', Info],
    ok: ['bg-signal-ok-soft text-signal-on-ok ring-signal-ok/20', CheckCircle2],
    warn: ['bg-signal-warn-soft text-signal-on-warn ring-signal-warn/20', AlertTriangle],
    bad: ['bg-signal-bad-soft text-signal-on-bad ring-signal-bad/20', AlertCircle],
  };
  const [cls, Icon] = map[tone];
  return (
    <div role={tone === 'bad' ? 'alert' : 'status'} className={cx('flex gap-3 rounded-2xl p-4 text-sm ring-1 ring-inset', cls, className)}>
      <Icon className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
      <div className="min-w-0">{title && <p className="font-semibold">{title}</p>}<div className={title ? 'mt-1' : ''}>{children}</div></div>
    </div>
  );
};

/* ── Form fields ────────────────────────────────────────────────────────── */
export function Field({ label, hint, error, children, optional, className }) {
  const id = useId();
  const describedBy = [hint && `${id}-hint`, error && `${id}-err`].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('space-y-1.5', className)}>
      {label && (
        <label htmlFor={id} className="flex items-baseline justify-between text-sm font-medium text-ink">
          <span>{label}</span>
          {optional && <span className="text-xs font-normal text-ink-faint">Optional</span>}
        </label>
      )}
      {React.cloneElement(children, { id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {hint && !error && <p id={`${id}-hint`} className="text-xs text-ink-muted">{hint}</p>}
      {error && <p id={`${id}-err`} className="flex items-center gap-1 text-xs font-medium text-signal-bad"><AlertCircle className="h-3.5 w-3.5" />{error}</p>}
    </div>
  );
}

const inputCls = 'block w-full rounded-2xl border-0 bg-surface px-4 py-3 text-[0.95rem] text-ink shadow-sm ring-1 ring-inset ring-line-strong placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-brand-500 aria-[invalid=true]:ring-signal-bad disabled:bg-paper-sunk disabled:text-ink-muted';

export const Input = forwardRef(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(inputCls, className)} {...rest} />;
});

export const Textarea = forwardRef(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(inputCls, 'min-h-[8rem]', className)} {...rest} />;
});

export const Select = forwardRef(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cx(inputCls, 'appearance-none bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-10', className)}
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%235B6577' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")" }}
      {...rest}>
      {children}
    </select>
  );
});

export function Checkbox({ checked, onChange, children, required, name, disabled, className }) {
  const id = useId();
  return (
    <div className={cx('flex items-start gap-3', className)}>
      <input id={id} name={name} type="checkbox" checked={checked} onChange={(e) => onChange?.(e.target.checked)}
        required={required} disabled={disabled}
        className="mt-0.5 h-5 w-5 flex-none cursor-pointer rounded-md border-line-strong accent-ink" />
      <label htmlFor={id} className="cursor-pointer text-sm leading-relaxed text-ink-soft">{children}</label>
    </div>
  );
}

export function Toggle({ checked, onChange, label, description, disabled }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-ink">{label}</label>
        {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
      </div>
      <button id={id} type="button" role="switch" aria-checked={checked} disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx('relative inline-flex h-7 w-12 flex-none items-center rounded-pill transition-colors duration-200',
          checked ? 'bg-primary' : 'bg-line-strong', disabled && 'cursor-not-allowed opacity-50')}>
        <span className={cx('inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200', checked ? 'translate-x-6' : 'translate-x-1')} />
      </button>
    </div>
  );
}

/* ── Segmented control (tabs) ───────────────────────────────────────────── */
export function Segmented({ options, value, onChange, className, ariaLabel }) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cx('inline-flex max-w-full overflow-x-auto rounded-pill bg-paper-sunk p-1 ring-1 ring-inset ring-line', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button key={o.value} role="tab" aria-selected={active} type="button" onClick={() => onChange(o.value)}
            className={cx('relative whitespace-nowrap rounded-pill px-4 py-2 text-sm font-medium transition-colors',
              active ? 'text-ink' : 'text-ink-muted hover:text-ink')}>
            {active && <m.span layoutId={`seg-${ariaLabel}`} className="absolute inset-0 rounded-pill bg-surface shadow-sm" transition={{ type: 'spring', bounce: 0, duration: 0.35 }} />}
            <span className="relative inline-flex items-center gap-1.5">{o.icon}{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Modal ──────────────────────────────────────────────────────────────── */
export function Modal({ open, onClose, title, description, children, footer, size = 'md', dismissible = true }) {
  const panelRef = useRef(null);
  const lastFocus = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => {
      const el = panelRef.current?.querySelector('input, select, textarea, button:not([data-close]), a[href]');
      (el || panelRef.current)?.focus({ preventScroll: true });
    }, 30);
    const onKey = (e) => {
      if (e.key === 'Escape' && dismissible) onClose?.();
      if (e.key === 'Tab' && panelRef.current) {
        const f = panelRef.current.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0]; const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      lastFocus.current?.focus?.();
    };
  }, [open, onClose, dismissible]);

  const widths = { sm: 'sm:max-w-md', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6">
          <m.div className="absolute inset-0 bg-night/50 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={dismissible ? onClose : undefined} aria-hidden="true" />
          <m.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} tabIndex={-1}
            className={cx('relative flex max-h-[92vh] max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-4xl bg-surface shadow-float outline-none sm:rounded-4xl', widths[size])}
            initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.35 }}>
            {(title || dismissible) && (
              <div className="flex flex-none items-start justify-between gap-4 px-6 pt-6 sm:px-8 sm:pt-8">
                <div>
                  {title && <h2 id={titleId} className="text-xl font-semibold tracking-tight">{title}</h2>}
                  {description && <p className="mt-1.5 text-sm text-ink-muted">{description}</p>}
                </div>
                {dismissible && (
                  <button data-close type="button" onClick={onClose} aria-label="Close"
                    className="-mr-2 -mt-1 rounded-full p-2 text-ink-muted transition hover:bg-paper-sunk hover:text-ink active:scale-95">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5 sm:px-8">{children}</div>
            {footer && <div className="flex flex-none flex-col-reverse gap-2 border-t border-line bg-paper px-6 py-4 sm:flex-row sm:justify-end sm:px-8">{footer}</div>}
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* ── Misc ───────────────────────────────────────────────────────────────── */
export const EmptyState = ({ icon: Icon, title, children, action }) => (
  <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-line-strong bg-paper px-6 py-12 text-center">
    {Icon && <Icon className="h-8 w-8 text-ink-faint" aria-hidden="true" />}
    <p className="mt-3 font-medium text-ink">{title}</p>
    {children && <div className="mt-1 max-w-sm text-sm text-ink-muted">{children}</div>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const PageLoader = ({ label = 'Loading' }) => (
  <div className="flex min-h-[50vh] items-center justify-center text-ink-muted" role="status">
    <Spinner className="h-6 w-6" /><span className="sr-only">{label}</span>
  </div>
);

export const Reveal = ({ children, delay = 0, className }) => (
  <m.div className={className} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }} transition={{ type: 'spring', bounce: 0, duration: 0.6, delay }}>
    {children}
  </m.div>
);

export const TextLink = ({ to, href, children, className }) => {
  const cls = cx('font-medium text-accent-strong underline decoration-accent/40 underline-offset-4 hover:decoration-accent-strong', className);
  return to ? <Link to={to} className={cls}>{children}</Link> : <a href={href} className={cls}>{children}</a>;
};
